'use strict';

// Build Detail related builds, completed for the whole Gallery (WI-27) and
// composed as one balanced area (WI-28) — see docs/work-items.md and
// snippets/dha-gallery-related.liquid, whose server-rendered markup and
// rules this mirrors exactly. The server can only see the first 50
// published builds; this script recomputes the area from the complete set
// (assets/dha-gallery-index.js) and swaps the markup only when the result
// differs, so pages whose relatives all sit in the first 50 are untouched.
//
// Rules (keep in sync with the snippet):
//  - Matching is case-insensitive on wheel, make and model (WI-26).
//  - "Same wheel" is built first (up to 6, published order, excluding the
//    build being viewed); "same vehicle" then excludes every build already
//    in it — a build never appears twice.
//  - With both groups each shows up to 4; a lone group shows up to 6.
//  - Cards keep one size across groups: each group's share of the width is
//    its card count (--dha-bd-n), and a lone group spans at least 4 slots
//    (--dha-bd-cols) so a single card is never stretched.
//  - The same-wheel heading links to "All N" when more exist than shown.
//  - With neither group, a quiet "browse every build" link closes the page.
// If the index can't be loaded the server-rendered area stays as it was.

(function () {
  var HARD_MAX = 6;
  var SPLIT_MAX = 4;

  function normKey(value) {
    return value == null ? '' : String(value).trim().toUpperCase();
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function buildGroup(kind, kicker, heading, cards, cols, moreHref, moreText) {
    var section = el('section', 'dha-bd__related dha-bd__related--' + kind);
    section.setAttribute('data-n', String(cards.length));
    section.style.setProperty('--dha-bd-n', String(cards.length));
    section.style.setProperty('--dha-bd-cols', String(cols));

    var head = el('header', 'dha-bd__related-head');
    head.appendChild(el('p', 'dha-bd__related-kicker', kicker));
    head.appendChild(el('h2', 'dha-bd__related-heading', heading));
    if (moreHref) {
      var more = el('a', 'dha-bd__related-more', moreText);
      more.href = moreHref;
      head.appendChild(more);
    }
    section.appendChild(head);

    var row = el('div', 'dha-bd__related-row');
    cards.forEach(function (c) {
      var a = el('a', 'dha-bd__related-card');
      a.href = c.url;
      if (c.image) {
        var img = el('img', 'dha-bd__related-img');
        img.src = c.image;
        img.alt = c.label;
        img.loading = 'lazy';
        img.width = 640;
        img.height = 480;
        a.appendChild(img);
      }
      a.appendChild(el('p', 'dha-bd__related-label', c.label));
      row.appendChild(a);
    });
    section.appendChild(row);
    return section;
  }

  function compute(wrap, builds) {
    var sid = wrap.getAttribute('data-build-sid');
    var make = wrap.getAttribute('data-make') || '';
    var model = wrap.getAttribute('data-model') || '';
    var wheel = wrap.getAttribute('data-wheel') || '';

    var others = builds.filter(function (b) { return String(b.sid) !== String(sid); });

    var wheelAll = normKey(wheel)
      ? others.filter(function (b) { return normKey(b.wheel) === normKey(wheel); })
      : [];
    var wheelKept = wheelAll.slice(0, HARD_MAX);
    var taken = {};
    wheelKept.forEach(function (b) { taken[b.sid] = true; });

    var vehicleKept = (normKey(make) && normKey(model))
      ? others.filter(function (b) {
          return !taken[b.sid] && normKey(b.make) === normKey(make) && normKey(b.model) === normKey(model);
        }).slice(0, HARD_MAX)
      : [];

    var limit = wheelKept.length > 0 && vehicleKept.length > 0 ? SPLIT_MAX : HARD_MAX;
    return {
      wheel: wheelKept.slice(0, limit),
      wheelTotal: wheelAll.length,
      vehicle: vehicleKept.slice(0, limit),
      make: make,
      model: model,
      wheelCode: wheel
    };
  }

  function render(wrap, result) {
    var next = el('div', 'dha-bd__lateral-wrap');
    next.setAttribute('data-dha-related', '');
    ['data-build-sid', 'data-make', 'data-model', 'data-wheel'].forEach(function (name) {
      next.setAttribute(name, wrap.getAttribute(name) || '');
    });
    var groups = (result.wheel.length ? 1 : 0) + (result.vehicle.length ? 1 : 0);
    next.setAttribute('data-groups', String(groups));

    function cols(n) { return groups === 1 ? Math.max(n, 4) : n; }

    if (result.wheel.length) {
      var more = result.wheelTotal > result.wheel.length;
      next.appendChild(buildGroup(
        'wheel',
        'Same wheel',
        'More on ' + result.wheelCode,
        result.wheel.map(function (b) { return { url: b.url, image: b.card, label: b.make + ' ' + b.model }; }),
        cols(result.wheel.length),
        more ? '/pages/gallery-builds?wheel=' + encodeURIComponent(result.wheelCode) : null,
        more ? 'All ' + result.wheelTotal + ' →' : null
      ));
    }
    if (result.vehicle.length) {
      next.appendChild(buildGroup(
        'vehicle',
        'Same vehicle',
        'Other ' + result.make + ' ' + result.model + ' builds',
        result.vehicle.map(function (b) { return { url: b.url, image: b.card, label: b.wheel }; }),
        cols(result.vehicle.length),
        null,
        null
      ));
    }
    if (groups === 0) {
      var browse = el('a', 'dha-bd__browse');
      browse.href = '/pages/gallery-builds';
      browse.appendChild(el('span', 'dha-bd__browse-text', 'Browse every published build'));
      var arrow = el('span', 'dha-bd__browse-arrow', '→');
      arrow.setAttribute('aria-hidden', 'true');
      browse.appendChild(arrow);
      next.appendChild(browse);
    }
    return next;
  }

  // What a visitor would see, as a comparable string: which groups, which
  // cards in which order, and the "All N" link.
  function signature(node) {
    var parts = [node.getAttribute('data-groups') || ''];
    Array.prototype.forEach.call(node.querySelectorAll('.dha-bd__related'), function (group) {
      var hrefs = Array.prototype.map.call(group.querySelectorAll('.dha-bd__related-card'), function (a) {
        return a.getAttribute('href');
      });
      var more = group.querySelector('.dha-bd__related-more');
      parts.push(group.className + ':' + hrefs.join(',') + ':' + (more ? more.textContent : ''));
    });
    return parts.join('|');
  }

  function update(wrap, builds) {
    var next = render(wrap, compute(wrap, builds));
    if (signature(next) !== signature(wrap) && wrap.parentNode) {
      wrap.parentNode.replaceChild(next, wrap);
    }
  }

  function init() {
    var wraps = document.querySelectorAll('[data-dha-related]');
    if (!wraps.length || !window.DHAGalleryIndex) return;
    window.DHAGalleryIndex.load().then(function (data) {
      Array.prototype.forEach.call(wraps, function (wrap) { update(wrap, data.builds); });
    }, function () { /* keep the server-rendered area */ });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
